import type { UserProfileTab } from '../components/profile/types'
import type { CommunityPostItem } from '@/api/modules/communityPost'
import type { HistoryItem } from '@/api/modules/customerServiceConversation'
import type { CustomerServiceTicket } from '@/api/modules/customerServiceTicket'
import type {
  FrontendUserAccountDeletionRequestItem,
  FrontendUserAvatarIdentityVerificationItem,
  FrontendUserItem,
  FrontendUserMessageRiskLogItem,
  FrontendUserPortraitPayload,
  FrontendUserProtocolAgreementItem,
} from '@/api/modules/frontendUser'
import type { GoodsItemAdminRecord } from '@/api/modules/goodsConfig'
import type { TradeOrderItem } from '@/api/modules/trade'
import type { ApiRequestError } from '@/api/requestError'
import { shallowReadonly, shallowRef } from 'vue'
import apiCommunityPost from '@/api/modules/communityPost'
import { historyPage } from '@/api/modules/customerServiceConversation'
import { ticketPage } from '@/api/modules/customerServiceTicket'
import apiFrontendUser from '@/api/modules/frontendUser'
import apiGoodsConfig from '@/api/modules/goodsConfig'
import apiTrade from '@/api/modules/trade'
import { normalizeApiRequestError } from '@/api/requestError'

interface PageData<T> {
  list: T[]
  total: number
}

function emptyPage<T>(): PageData<T> {
  return { list: [], total: 0 }
}

export function useUserDetail() {
  const user = shallowRef<FrontendUserItem | null>(null)
  const portrait = shallowRef<FrontendUserPortraitPayload | null>(null)
  const posts = shallowRef<PageData<CommunityPostItem>>(emptyPage())
  const goods = shallowRef<PageData<GoodsItemAdminRecord>>(emptyPage())
  const buyerOrders = shallowRef<PageData<TradeOrderItem>>(emptyPage())
  const sellerOrders = shallowRef<PageData<TradeOrderItem>>(emptyPage())
  const protocols = shallowRef<PageData<FrontendUserProtocolAgreementItem>>(emptyPage())
  const risks = shallowRef<PageData<FrontendUserMessageRiskLogItem>>(emptyPage())
  const deletions = shallowRef<PageData<FrontendUserAccountDeletionRequestItem>>(emptyPage())
  const avatarIdentityVerifications = shallowRef<FrontendUserAvatarIdentityVerificationItem[]>([])
  const conversations = shallowRef<PageData<HistoryItem>>(emptyPage())
  const tickets = shallowRef<PageData<CustomerServiceTicket>>(emptyPage())
  const detailLoading = shallowRef(false)
  const tabLoading = shallowRef(false)
  const detailError = shallowRef<ApiRequestError | null>(null)
  const tabError = shallowRef<ApiRequestError | null>(null)
  const serviceUnavailable = shallowRef(false)
  const loadedTabs = shallowRef(new Set<UserProfileTab>())
  let requestEpoch = 0

  function resetRelatedData() {
    portrait.value = null
    posts.value = emptyPage()
    goods.value = emptyPage()
    buyerOrders.value = emptyPage()
    sellerOrders.value = emptyPage()
    protocols.value = emptyPage()
    risks.value = emptyPage()
    deletions.value = emptyPage()
    avatarIdentityVerifications.value = []
    conversations.value = emptyPage()
    tickets.value = emptyPage()
    serviceUnavailable.value = false
    loadedTabs.value = new Set()
  }

  async function open(userId: number) {
    const epoch = ++requestEpoch
    resetRelatedData()
    user.value = null
    detailLoading.value = true
    detailError.value = null
    tabError.value = null
    try {
      const nextUser = (await apiFrontendUser.detail(userId)).data
      if (epoch !== requestEpoch) {
        return
      }
      user.value = nextUser
      await loadTab('overview')
    }
    catch (reason) {
      if (epoch === requestEpoch) {
        detailError.value = normalizeApiRequestError(reason)
      }
    }
    finally {
      if (epoch === requestEpoch) {
        detailLoading.value = false
      }
    }
  }

  async function ensurePortrait(userId: number, epoch: number) {
    if (!portrait.value) {
      const nextPortrait = (await apiFrontendUser.portrait(userId)).data
      if (epoch === requestEpoch && user.value?.id === userId) {
        portrait.value = nextPortrait
      }
    }
  }

  async function loadTab(tab: UserProfileTab) {
    const userId = user.value?.id
    if (!userId || loadedTabs.value.has(tab)) {
      return
    }

    const epoch = requestEpoch
    const isCurrent = () => epoch === requestEpoch && user.value?.id === userId
    tabLoading.value = true
    tabError.value = null
    try {
      if (tab === 'overview' || tab === 'social') {
        await ensurePortrait(userId, epoch)
      }
      else if (tab === 'content') {
        const response = await apiCommunityPost.page({ user_id: userId, page: 1, per_page: 10 })
        if (!isCurrent()) {
          return
        }
        posts.value = { list: response.data.list ?? [], total: response.data.total ?? 0 }
      }
      else if (tab === 'c2c') {
        const [goodsResponse, buyerResponse, sellerResponse] = await Promise.all([
          apiGoodsConfig.item.page({ seller_user_id: userId, page: 1, per_page: 10 }),
          apiTrade.order.page({ buyer_user_id: userId, page: 1, per_page: 10 }),
          apiTrade.order.page({ seller_user_id: userId, page: 1, per_page: 10 }),
        ])
        if (!isCurrent()) {
          return
        }
        goods.value = { list: goodsResponse.data.list ?? [], total: goodsResponse.data.total ?? 0 }
        buyerOrders.value = { list: buyerResponse.data.list ?? [], total: buyerResponse.data.total ?? 0 }
        sellerOrders.value = { list: sellerResponse.data.list ?? [], total: sellerResponse.data.total ?? 0 }
      }
      else if (tab === 'safety') {
        const [protocolResponse, riskResponse, deletionResponse, avatarIdentityResponse] = await Promise.all([
          apiFrontendUser.protocolAgreements(userId, { page: 1, per_page: 10 }),
          apiFrontendUser.messageRiskLogs(userId, { page: 1, per_page: 10 }),
          apiFrontendUser.accountDeletionRequests(userId, { page: 1, per_page: 10 }),
          apiFrontendUser.avatarIdentityVerifications(userId),
        ])
        if (!isCurrent()) {
          return
        }
        protocols.value = { list: protocolResponse.data.list ?? [], total: protocolResponse.data.total ?? 0 }
        risks.value = { list: riskResponse.data.list ?? [], total: riskResponse.data.total ?? 0 }
        deletions.value = { list: deletionResponse.data.list ?? [], total: deletionResponse.data.total ?? 0 }
        avatarIdentityVerifications.value = avatarIdentityResponse.data ?? []
      }
      else if (tab === 'service') {
        const [conversationResult, ticketResult] = await Promise.allSettled([
          historyPage({ keyword: String(userId), page: 1, per_page: 10 }),
          ticketPage({ user_id: userId, page: 1, per_page: 10 }),
        ])
        if (!isCurrent()) {
          return
        }
        if (conversationResult.status === 'fulfilled') {
          conversations.value = conversationResult.value.data
        }
        if (ticketResult.status === 'fulfilled') {
          tickets.value = ticketResult.value.data
        }
        serviceUnavailable.value = conversationResult.status === 'rejected' && ticketResult.status === 'rejected'
      }
      if (isCurrent()) {
        loadedTabs.value = new Set([...loadedTabs.value, tab])
      }
    }
    catch (reason) {
      if (isCurrent()) {
        tabError.value = normalizeApiRequestError(reason)
      }
    }
    finally {
      if (epoch === requestEpoch) {
        tabLoading.value = false
      }
    }
  }

  // 后台撤销认证后单独刷新记录，避免重置当前详情页签和其他已加载数据。
  async function reloadAvatarIdentityVerifications() {
    const userId = user.value?.id
    if (!userId) {
      return
    }
    const response = await apiFrontendUser.avatarIdentityVerifications(userId)
    if (user.value?.id === userId) {
      avatarIdentityVerifications.value = response.data ?? []
    }
  }

  return {
    user: shallowReadonly(user),
    portrait: shallowReadonly(portrait),
    posts: shallowReadonly(posts),
    goods: shallowReadonly(goods),
    buyerOrders: shallowReadonly(buyerOrders),
    sellerOrders: shallowReadonly(sellerOrders),
    protocols: shallowReadonly(protocols),
    risks: shallowReadonly(risks),
    deletions: shallowReadonly(deletions),
    avatarIdentityVerifications: shallowReadonly(avatarIdentityVerifications),
    conversations: shallowReadonly(conversations),
    tickets: shallowReadonly(tickets),
    detailLoading: shallowReadonly(detailLoading),
    tabLoading: shallowReadonly(tabLoading),
    detailError: shallowReadonly(detailError),
    tabError: shallowReadonly(tabError),
    serviceUnavailable: shallowReadonly(serviceUnavailable),
    open,
    loadTab,
    reloadAvatarIdentityVerifications,
  }
}
